// src/components/ui/emptyState/emptyStates.ts
import React from 'react';
import type { EmptyStateProps } from './EmptyState';
import { IconNote, IconArSpaceOutline, IconPepiconsPrintGift } from '@/components/icons';

export type ActionKind = 'createPost' | 'login' | 'clearFilters' | 'explore';

/** ✅ 對齊你 Button 的 props */
export type ButtonUISpec = {
  size?: 'lg' | 'md' | 'sm';
  color?: 'red' | 'green' | 'orange' | 'dark' | 'light' | 'gray' | 'brand' | 'disabled';
  width?: 'fit' | 'expand';
};

export type EmptyActionSpec = {
  kind: ActionKind;
  label: React.ReactNode;
  ui?: ButtonUISpec;
};

/** preset：集中 icon/title/description/tone/actionSpec（外觀意圖），不放行為 */
export type EmptyPreset = Omit<EmptyStateProps, 'show' | 'action' | 'data-testid'> & {
  actionSpec?: EmptyActionSpec;
};

type IconComp = React.ComponentType<{ width?: number; height?: number; className?: string }>;
function makeIcon(Comp: IconComp, props: React.ComponentProps<IconComp>) {
  return React.createElement(Comp, props);
}

const base: Pick<EmptyPreset, 'align' | 'size' | 'tone' | 'ariaLive'> = {
  align: 'center',
  size: 'md',
  tone: 'muted',
  ariaLive: 'polite',
};

export const EMPTY = {
  article: {
    /** 互動貼文：尚無資料 */
    noPosts: {
      ...base,
      icon: makeIcon(IconNote, { width: 70, className: 'text-grey' }),
      title: '尚無互動貼文',
    } satisfies EmptyPreset,
  },

  arSpace: {
    /** AR列表：尚無AR資料 */
    noPosts: {
      ...base,
      icon: makeIcon(IconArSpaceOutline, { width: 70, className: 'text-grey' }),
      title: '尚無AR空間',
    } satisfies EmptyPreset,
  },

  gift: {
    /** 禮物列表：尚無禮物 */
    noGift: {
      ...base,
      icon: makeIcon(IconPepiconsPrintGift, { width: 70, className: 'text-grey' }),
      title: '您尚未有禮物',
      // actionSpec: {
      //   kind: 'explore',
      //   label: '去逛逛',
      //   ui: { size: 'md', color: 'red', width: 'fit' },
      // },
    } satisfies EmptyPreset,
  },

  search: {
    /** 搜尋：無結果 */
    noResults: {
      ...base,
      icon: makeIcon(IconNote, { width: 70, className: 'text-grey' }),
      title: '找不到符合的結果',
      titleClassName: 'text-muted',
      description: '試試看換個關鍵字或清除篩選條件。',
      descriptionClassName: 'text-grey',
      actionSpec: {
        kind: 'clearFilters',
        label: '清除篩選',
        ui: { size: 'md', color: 'gray', width: 'fit' },
      },
    } satisfies EmptyPreset,
  },
} as const;
